import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Youtube, Instagram, Music2 } from "lucide-react";

/**
 * Renders any social embed present on a blog article payload.
 * The backend hands us a normalised `embed` object with optional
 * `youtube.embed_url`, `tiktok.url`, `instagram.url`.
 *
 * TikTok + Instagram both rely on their official <blockquote> + script
 * pattern. We lazy-inject each provider script once per session · this
 * keeps the article page snappy and avoids double-loading.
 */
const TIKTOK_SRC = "https://www.tiktok.com/embed.js";
const INSTAGRAM_SRC = "https://www.instagram.com/embed.js";

function ensureScript(src) {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[src="${src}"]`)) {
    // Re-trigger Instagram parsing when navigating between articles.
    if (src === INSTAGRAM_SRC && window.instgrm?.Embeds?.process) {
      window.instgrm.Embeds.process();
    }
    return;
  }
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  document.body.appendChild(s);
}

export default function SocialEmbed({ embed }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);

  useEffect(() => {
    if (embed?.tiktok?.url) ensureScript(TIKTOK_SRC);
    if (embed?.instagram?.url) ensureScript(INSTAGRAM_SRC);
  }, [embed?.tiktok?.url, embed?.instagram?.url]);

  if (!embed || (!embed.youtube && !embed.tiktok && !embed.instagram && !embed.soundcloud)) {
    return null;
  }

  return (
    <div ref={containerRef} className="flex flex-col gap-6 my-8" data-testid="blog-embeds">
      {embed.youtube && (
        <figure
          className="rounded-xl overflow-hidden border border-white/10 bg-black/40"
          data-testid="blog-embed-youtube"
        >
          <div className="relative pt-[56.25%]">
            <iframe
              title="YouTube video"
              src={embed.youtube.embed_url}
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <figcaption className="flex items-center justify-between px-4 py-2 text-xs text-[color:var(--fg-muted)]">
            <span className="inline-flex items-center gap-1.5">
              <Youtube size={13} className="text-red-400" /> YouTube
            </span>
            <a
              href={embed.youtube.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-white transition-colors"
              data-testid="blog-embed-youtube-open"
            >
              {t("blog.watch_youtube")} <ExternalLink size={11} />
            </a>
          </figcaption>
        </figure>
      )}

      {embed.tiktok?.url && (
        <div
          className="rounded-xl border border-white/10 bg-black/40 p-3"
          data-testid="blog-embed-tiktok"
        >
          <blockquote
            className="tiktok-embed mx-auto"
            cite={embed.tiktok.url}
            data-video-id=""
            style={{ maxWidth: 605, minWidth: 280 }}
          >
            <a href={embed.tiktok.url} target="_blank" rel="noopener noreferrer">
              {t("blog.open_tiktok")}
            </a>
          </blockquote>
          <div className="text-center mt-1">
            <a
              href={embed.tiktok.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[color:var(--fg-muted)] hover:text-white"
              data-testid="blog-embed-tiktok-open"
            >
              {t("blog.open_tiktok")} <ExternalLink size={11} />
            </a>
          </div>
        </div>
      )}

      {embed.instagram?.url && (
        <div
          className="rounded-xl border border-white/10 bg-black/40 p-3"
          data-testid="blog-embed-instagram"
        >
          <blockquote
            className="instagram-media mx-auto"
            data-instgrm-permalink={embed.instagram.url}
            data-instgrm-version="14"
            style={{
              background: "#000",
              border: 0,
              margin: "0 auto",
              maxWidth: 540,
              minWidth: 280,
              width: "100%",
            }}
          >
            <a href={embed.instagram.url} target="_blank" rel="noopener noreferrer">
              {t("blog.open_instagram")}
            </a>
          </blockquote>
          <div className="text-center mt-1">
            <a
              href={embed.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[color:var(--fg-muted)] hover:text-white"
              data-testid="blog-embed-instagram-open"
            >
              <Instagram size={12} /> {t("blog.open_instagram")} <ExternalLink size={11} />
            </a>
          </div>
        </div>
      )}
      {embed.soundcloud?.url && (
        <figure
          className="rounded-xl overflow-hidden border border-white/10 bg-black/40"
          data-testid="blog-embed-soundcloud"
        >
          <iframe
            title="SoundCloud"
            width="100%"
            height="450"
            scrolling="no"
            frameBorder="no"
            allow="autoplay"
            src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(
              embed.soundcloud.url
            )}&color=%238B5CF6&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=true&visual=true`}
          />
          <figcaption className="flex items-center justify-between px-4 py-2 text-xs text-[color:var(--fg-muted)]">
            <span className="inline-flex items-center gap-1.5">
              <Music2 size={13} className="text-[#ff5500]" /> SoundCloud
            </span>
            <a
              href={embed.soundcloud.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-white transition-colors"
              data-testid="blog-embed-soundcloud-open"
            >
              {t("blog.open_soundcloud")} <ExternalLink size={11} />
            </a>
          </figcaption>
        </figure>
      )}
    </div>
  );
}
